import React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";
import {
  Sparkles, Loader2, Plus, Bug, Palette,
  Code, X, Rocket, ArrowRight,
  Database, Key, FolderTree,   ChevronRight, FileCode, RefreshCw,
  CheckCircle2, XCircle, Wrench,   ChevronDown, ChevronUp, AlertTriangle, Undo2, Download, Github, Copy, ExternalLink, ArrowDownToLine, MoreHorizontal, CreditCard,
  Monitor, Smartphone, Tablet, MousePointer, BookOpen, History, Cloud, Lightbulb
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { filterProtectedPaths } from "@/lib/patchGuard";
import { AIEditPlanDisplay } from "./AIEditPlanDisplay";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { ExplainAppDialog } from "./ExplainAppDialog";
import { VersionHistoryDialog } from "./VersionHistoryDialog";
import { VersionHistoryPanel } from "./VersionHistoryPanel";
import { ApiKeyManager } from "@/components/ApiKeyManager";
import { ProjectMemory } from "@/hooks/useProjectMemory";
import { ProjectSnapshot } from "@/hooks/useProjectSnapshots";
import { WorkspaceOnboarding } from "./WorkspaceOnboarding";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { getOrCreateActiveSessionId } from "@/lib/activeSessionId";
import { DayOneCloudPanel } from "./DayOneCloudPanel";
import { VisualEditor } from "./VisualEditor";
import { DesignPanel } from "./DesignPanel";
import { ProjectKnowledgePanel } from "./ProjectKnowledgePanel";
import type { StreamingProgress, PlanPreview, OrchestrationResult } from "@/hooks/useAICodeAgent";
import type { SelfHealState } from "@/hooks/useSelfHealingPreview";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SecurityAlert } from "./SecurityAlert";
import { TypewriterText } from "@/components/ui/TypewriterText";
import { PreviewErrorBoundary } from "@/components/PreviewErrorBoundary";

interface Message {
  role: "user" | "assistant";
  content: string;
  editPlan?: any;
  timestamp?: Date;
}

interface FileNode {
  name: string;
  isFolder?: boolean;
}

interface Project {
  id: string;
  name: string;
  updated_at?: string;
  status?: 'building' | 'ready';
  idea_id?: string | null;
}

interface BuilderWorkspaceStateProps {
  project: Project;
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onNewProject: () => void;
  onRenameProject: (projectId: string, newName: string) => Promise<boolean>;
  onDeleteProject: (projectId: string) => Promise<boolean>;
  onDuplicateProject: (projectId: string) => Promise<any>;
  files: FileNode[];
  selectedFile: string | null;
  onSelectFile: (filename: string) => void;
  previewUrl: string | null;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewKey: number;
  /** Same sessionId used when saving preview files; ensures iframe reads the key we wrote to. */
  previewSessionId?: string | null;
  isPreviewAvailable?: boolean;
  messages: Message[];
  builderStatus: "idle" | "generating" | "applying" | "building" | "fixing" | "polishing" | "success" | "failed";
  onSendMessage: (message: string) => void;
  onApplyEdits: (edits: any[]) => Promise<any>;
  lastPreviewUpdatedAt?: number | null;
  editorContent: string;
  onEditorChange: (value: string) => void;
  onEditorMount: (editor: any) => void;
  onDeploy: () => void;
  isDeploying: boolean;
  previewReady?: boolean;
  onRefreshPreview?: () => void;
  onPreviewReady?: () => void;
  memory: ProjectMemory | null;
  isMemoryLoading: boolean;
  snapshots: ProjectSnapshot[];
  snapshotsLoading: boolean;
  onRestoreSnapshot: (snapshot: ProjectSnapshot) => Promise<void>;
  onDeleteSnapshot: (snapshotId: string) => Promise<boolean>;
  buildValidationError?: string | null;
  buildValidationBucket?: string | null;
  onFixBuildClick?: () => void;
  fixAttempted?: boolean;
  lastApplySummary?: string | null;
  lastPrompt?: string;
  onRetryLastChange?: () => void;
  onRetryWithinTargets?: () => void;
  onRevertLastChange?: () => void;
  onExportZip?: () => void;
  githubConnected?: boolean;
  githubHasPat?: boolean;
  onOpenGitHubConnect?: () => void;
  onOpenGitHubPush?: () => void;
  githubPushRunning?: boolean;
  projectFileContents?: Record<string, string>;
  onDismissPlan?: () => void;
  buildLog?: Array<{
    id: string;
    ts: number;
    status: "generating" | "applying" | "building" | "fixing" | "polishing" | "success" | "failed";
    prompt?: string;
    summary?: string;
    error?: string;
  }>;
  promptPreFill?: string;
  waitingForScaffold?: boolean;
  buildStartedThisSession?: boolean;
  runtimeErrorSnippet?: string | null;
  lastPreviewReadyAt?: number | null;
  onPlanGenerated?: () => void;
  entrySource?: "idea" | "create";
  onPlanAutoGenerated?: (source?: "idea" | "create") => void;
  buildReport?: {
    runId: string;
    status: "success" | "failed";
    startedAt: number;
    finishedAt: number;
    userPromptSnippet: string;
    usedPlan: boolean;
    targetPaths: string[];
    changedPaths: string[];
    previewReadyAt?: number | null;
    errorBucket?: string | null;
    lastErrorCode?: string | null;
    landingScopeDroppedCount?: number;
    landingScopeDroppedPathsSample?: string[];
  } | null;
  onCopyBuildReport?: () => void;
  shouldShowOnboarding?: boolean;
  onDismissOnboarding?: () => void;
  lastRepo?: { owner: string; repo: string; html_url: string } | null;
  /** Screen stubs created by sandbox (in-memory only); show warning when length > 0 */
  createdScreenStubs?: string[];
  /** Streaming progress from Claude API — shows files as they're generated */
  streamingProgress?: StreamingProgress;
  /** Pre-response plan preview — shows what AI intends to build before generating */
  planPreview?: PlanPreview | null;
  /** Last successful deploy URL for this project */
  lastDeployUrl?: string | null;
  /** Self-healing state from useSelfHealingPreview */
  selfHealState?: SelfHealState;
  /** Trigger a manual AI fix after auto-fix failed */
  onSelfHealFix?: () => void;
  /** Clear all errors and let user start a new prompt */
  onStartFresh?: () => void;
  /** Security alerts from secret scanner */
  securityAlerts?: Array<{
    filePath: string;
    lineNumber: number;
    secretType: string;
    match: string;
    severity: "critical" | "warning" | "info";
  }>;
  /** Dismiss security alerts */
  onDismissSecurityAlerts?: () => void;
  /** V3 Streaming narrative messages */
  narrativeMessages?: Array<{
    text: string;
    step: string;
    ts: number;
    description?: string;
    descriptions?: string[];
    files?: string[];
  }>;
  /** Chat mode: talk without triggering builds */
  chatMode?: boolean;
  /** Toggle chat mode */
  onChatModeChange?: (enabled: boolean) => void;
  /** Chat mode loading state */
  chatLoading?: boolean;
}

const PLAN_STORAGE_KEY = (projectId: string) => `dayone.plan.${projectId}`;

function isSubstantialPrompt(prompt: string | undefined): boolean {
  if (!prompt?.trim()) return false;
  const t = prompt.trim();
  return t.length >= 40 || t.split(/\s+/).filter(Boolean).length >= 6;
}

export type StartupPlan = {
  problem: string;
  icp: string;
  mvpFeatures: string[];
  monetization: string;
  keyScreens: string[];
  dataModel: string[];
  fileTree: string[];
};

type StoredPlan = { plan: StartupPlan; generatedAt: number };

type UserStatusTone = "neutral" | "busy" | "warning" | "error";

/** Pattern 3: Two-line status card for narrative events */
function NarrativeStatusCard({
  text,
  description,
  isActive,
  showTypewriter
}: {
  text: string;
  description?: string;
  isActive: boolean;
  showTypewriter?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-slate-50/80 animate-fade-in">
      <div className="mt-0.5 flex-shrink-0">
        {isActive ? (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#7c6af0" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#7c6af0" }} />
          </span>
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#16a34a" }} />
        )}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-normal" style={{ color: isActive ? "#18151f" : "#6b5f8a" }}>
          {showTypewriter ? <TypewriterText text={text} speed={80} /> : text}
        </span>
        {description && (
          <span className="text-xs truncate" style={{ color: "#9ca3af" }}>
            {description}
          </span>
        )}
      </div>
    </div>
  );
}

function getUserStatus(
  builderStatus: "idle" | "generating" | "applying" | "building" | "fixing" | "polishing" | "success" | "failed",
  opts: {
    waitingForScaffold?: boolean;
    buildStartedThisSession?: boolean;
    isPreviewAvailable?: boolean;
    previewLoadTimedOut?: boolean;
    previewReady?: boolean;
  }
): { label: string; tone: UserStatusTone } {
  const { waitingForScaffold, buildStartedThisSession, isPreviewAvailable, previewLoadTimedOut, previewReady } = opts;
  if (waitingForScaffold && buildStartedThisSession) return { label: "Generating files…", tone: "busy" };
  if (previewLoadTimedOut) return { label: "Preview taking too long", tone: "warning" };
  if (builderStatus === "failed") return { label: "Needs attention", tone: "error" };
  if (builderStatus === "polishing") return { label: "Polishing your app…", tone: "busy" };
  if (builderStatus === "fixing") return { label: "Fixing…", tone: "busy" };
  if (builderStatus === "generating" || builderStatus === "applying" || builderStatus === "building") return { label: "Generating…", tone: "busy" };
  if (isPreviewAvailable && !previewReady && !previewLoadTimedOut) return { label: "Loading preview…", tone: "busy" };
  return { label: "Ready", tone: "neutral" };
}

const quickActions = [
  { label: "Add auth", icon: Key, prompt: "Add authentication to this app. Create a login page with email/password sign in and sign up tabs using Card and Tabs components. Wrap routes in AuthProvider from @/contexts/AuthContext. Add a ProtectedRoute wrapper that redirects to login if not authenticated. Show the user's email in the Shell sidebar and add a Sign Out button." },
  { label: "Add database", icon: Database, prompt: "Add Supabase database integration to this app. Replace all localStorage usage with the db helper from @/lib/database. Create TypeScript interfaces for each entity. Add loading states and error handling. Include the SQL schema as a comment block at the top of App.tsx with CREATE TABLE statements and RLS policies. If auth is already added, scope all data to the current user with user_id." },
  { label: "Add feature", icon: Plus, prompt: "Add a feature that " },
  { label: "Change design", icon: Palette, prompt: "Change the design to " },
  { label: "Fix issue", icon: Bug, prompt: "Fix the issue where " },
  { label: "Add Stripe", icon: CreditCard, prompt: "Add Stripe subscription payments to this app. Create a pricing page with Free, Pro, and Enterprise plan cards using Card components. Add a checkout flow that calls an API endpoint with { priceId } to create a Stripe Checkout Session and redirects to the returned URL. Create CheckoutSuccess and CheckoutCancel pages. Show subscription status in settings with current tier, next billing date, and Manage Subscription link. In preview mode (no VITE_STRIPE_PUBLISHABLE_KEY), the pricing page must render — show a toast when Subscribe is clicked without keys configured." },
];

// Pattern 4A: Contextual suggestions based on detected industry (post-build)
const CONTEXTUAL_SUGGESTIONS: Record<string, Array<{ label: string; prompt: string }>> = {
  saas: [
    { label: "Add team workspace", prompt: "Add a team members page where you can invite people by email and see a list of current members with their join date" },
    { label: "Add activity feed", prompt: "Add an activity feed that shows recent actions like 'Sarah updated Project Alpha' with timestamps and user avatars" },
    { label: "Add user settings", prompt: "Add a settings page with profile editing, notification preferences (email/push toggles), and a danger zone for account deletion" },
  ],
  ecommerce: [
    { label: "Add shopping cart", prompt: "Add a shopping cart with quantity controls, item removal, and subtotal calculation, accessible from a cart icon in the header" },
    { label: "Add wishlist", prompt: "Add a wishlist feature where users can save products, see them on a dedicated page, and move items to cart with one click" },
    { label: "Add order tracking", prompt: "Add an order tracking page showing order status (Confirmed → Shipped → Delivered) with estimated delivery date and a timeline visualization" },
  ],
  fitness: [
    { label: "Add workout logging", prompt: "Add a workout log where users can record exercises with sets, reps, and weight, organized by date with a calendar view" },
    { label: "Add progress charts", prompt: "Add a progress dashboard with line charts showing weight lifted over time, workout frequency per week, and personal records" },
    { label: "Add exercise library", prompt: "Add an exercise library with muscle group filters, instructional GIFs or images, and the ability to add exercises to today's workout" },
  ],
  healthcare: [
    { label: "Add appointment booking", prompt: "Add appointment booking with a calendar picker, available time slots, and doctor selection" },
    { label: "Add medication tracker", prompt: "Add a medication tracker where patients log daily doses, see adherence percentage, and get visual reminders for missed doses" },
    { label: "Add health metrics", prompt: "Add a health metrics dashboard for tracking blood pressure, heart rate, and weight with trend charts and normal range indicators" },
  ],
  education: [
    { label: "Add quiz builder", prompt: "Add a quiz feature with multiple-choice questions, timed responses, and instant scoring with correct answer reveals" },
    { label: "Add progress tracking", prompt: "Add a student progress page showing completed lessons, quiz scores, time spent learning, and a completion percentage badge" },
    { label: "Add discussion board", prompt: "Add a discussion board where students post questions, instructors reply with pinned answers, and users can upvote helpful responses" },
  ],
  fintech: [
    { label: "Add transaction history", prompt: "Add a transaction history page showing all payments with date, amount, status, and a filter by date range" },
    { label: "Add invoice generator", prompt: "Add an invoice creator with client selection, line items, tax calculation, and PDF export" },
    { label: "Add payment dashboard", prompt: "Add a dashboard showing total revenue, pending payments, and a chart of income over time" },
  ],
};

// Generic fallback for industries not in the map
const GENERIC_SUGGESTIONS = [
  { label: "Add search", prompt: "Add a search bar with real-time filtering, highlighted matches, and keyboard navigation through results" },
  { label: "Add notifications", prompt: "Add a notification bell with unread count badge, a dropdown showing recent alerts, and mark-all-as-read functionality" },
  { label: "Add user profiles", prompt: "Add user profile pages with name, bio, location, and a public activity history showing recent actions" },
];

// Static pills for pre-build state (no industry detected yet)
const STATIC_PILLS = [
  { label: "Make it better", emoji: "✨" },
  { label: "Fix errors", emoji: "🔧" },
  { label: "Add auth", emoji: "🔐" },
  { label: "Add dark mode", emoji: "🌙" },
] as const;

function getLanguage(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase();
  const map: Record<string, string> = {
    ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript",
    json: "json", html: "html", css: "css", md: "markdown",
  };
  return map[ext || ""] || "plaintext";
}

export function BuilderWorkspaceState({
  project,
  projects,
  onSelectProject,
  onNewProject,
  onRenameProject,
  onDeleteProject,
  onDuplicateProject,
  files,
  selectedFile,
  onSelectFile,
  previewUrl,
  iframeRef,
  previewKey,
  previewSessionId = null,
  isPreviewAvailable = true,
  messages,
  builderStatus,
  onSendMessage,
  onApplyEdits,
  lastPreviewUpdatedAt = null,
  editorContent,
  onEditorChange,
  onEditorMount,
  onDeploy,
  isDeploying,
  previewReady = false,
  onRefreshPreview,
  onPreviewReady,
  memory,
  isMemoryLoading,
  snapshots,
  snapshotsLoading,
  onRestoreSnapshot,
  onDeleteSnapshot,
  buildValidationError = null,
  buildValidationBucket = null,
  onFixBuildClick,
  fixAttempted = false,
  lastApplySummary = null,
  lastPrompt = "",
  onRetryLastChange,
  onRetryWithinTargets,
  onRevertLastChange,
  onExportZip,
  githubConnected = false,
  githubHasPat = false,
  onOpenGitHubConnect,
  onOpenGitHubPush,
  githubPushRunning = false,
  projectFileContents = {},
  onDismissPlan,
  buildLog = [],
  promptPreFill,
  waitingForScaffold = false,
  buildStartedThisSession = false,
  runtimeErrorSnippet = null,
  lastPreviewReadyAt = null,
  onPlanGenerated,
  entrySource,
  onPlanAutoGenerated,
  buildReport = null,
  onCopyBuildReport,
  shouldShowOnboarding = false,
  onDismissOnboarding,
  lastRepo = null,
  createdScreenStubs = [],
  streamingProgress,
  planPreview,
  lastDeployUrl,
  selfHealState,
  onSelfHealFix,
  onStartFresh,
  securityAlerts = [],
  onDismissSecurityAlerts,
  narrativeMessages = [],
  chatMode = false,
  onChatModeChange,
  chatLoading = false,
}: BuilderWorkspaceStateProps) {
  const isApplying = builderStatus === "applying";
  const isBuilding = builderStatus === "building";
  const isFixing = builderStatus === "fixing";
  const isPolishing = builderStatus === "polishing";
  const isSelfHealing = selfHealState?.status === "fixing";
  // Show polishing overlay when either builderStatus=polishing OR selfHealState is fixing
  const showPolishingOverlay = isPolishing || isSelfHealing;
  const isBusy = (builderStatus !== "idle" && builderStatus !== "success" && builderStatus !== "failed") || chatLoading;

  const [previewLoadTimedOut, setPreviewLoadTimedOut] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userStatus = getUserStatus(builderStatus, {
    waitingForScaffold,
    buildStartedThisSession,
    isPreviewAvailable,
    previewLoadTimedOut,
    previewReady,
  });
  const [input, setInput] = useState("");
  // Overlay panel state — null means closed, otherwise shows the selected panel sliding in from right
  const [activeOverlay, setActiveOverlay] = useState<'files' | 'cloud' | 'versions' | 'knowledge' | null>(null);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile' | 'tablet'>('desktop');
  const [visualEditMode, setVisualEditMode] = useState(false);
  const [designPanelOpen, setDesignPanelOpen] = useState(false);
  // Pattern 4A: Persist industry for contextual suggestions (survives edits within session)
  const [persistedIndustry, setPersistedIndustry] = useState<string | null>(null);
  const didAutoPlanRef = useRef(false);
  const [planData, setPlanData] = useState<StoredPlan | null>(() => {
    if (typeof localStorage === "undefined" || !project?.id) return null;
    try {
      const raw = localStorage.getItem(PLAN_STORAGE_KEY(project.id));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredPlan;
      return parsed?.plan && typeof parsed.generatedAt === "number" ? parsed : null;
    } catch { return null; }
  });
  const [expandedPlanIndex, setExpandedPlanIndex] = useState<number | null>(null);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);
  const appliedPreFillRef = useRef(false);
  const autoSentRef = useRef(false);
  const onSendMessageRef = useRef(onSendMessage);
  onSendMessageRef.current = onSendMessage;
  const buildErrorRef = useRef<HTMLDivElement>(null);
  // Increased to 5 minutes for complex builds (40+ files take 2-4 min to bundle)
  const PREVIEW_LOAD_TIMEOUT_MS = 300000;
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userHasScrolledUpRef = useRef(false);
  const chatInputContainerRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const headerActionsRef = useRef<HTMLDivElement>(null);
  const [onboardingStep, setOnboardingStep] = useState(0);
  useEffect(() => {
    if (promptPreFill && promptPreFill.trim() && !appliedPreFillRef.current) {
      // Don't prefill input on returning visits — but allow for create source
      if (files.length > 0 && entrySource !== "create") {
        appliedPreFillRef.current = true;
        return;
      }
      setInput(promptPreFill.trim());
      appliedPreFillRef.current = true;
      textareaRef.current?.focus();
    }
  }, [promptPreFill, files.length, entrySource]);

  // Auto-send: one-shot ref-based approach (no timer, no cleanup).
  // Fires exactly once when all conditions are simultaneously true.
  useEffect(() => {
    if (autoSentRef.current) return;
    if (entrySource !== "create" && entrySource !== "plan_mode_approved" && entrySource !== "plan_mode_skipped") {
      // Non-create/plan_mode source with existing files = returning visit, suppress auto-send
      if (files.length > 0) { autoSentRef.current = true; }
      return;
    }
    if (!promptPreFill?.trim()) return;
    if (!project?.id) return;
    if (isBusy) return;
    if (files.length < 5) return; // scaffold not loaded yet
    // All conditions met — fire once
    autoSentRef.current = true;
    const msg = promptPreFill.trim();
    console.log("[auto-send] FIRED (one-shot)", { projectId: project.id, fileCount: files.length, prompt: msg.slice(0, 80) });
    onSendMessageRef.current(msg);
    setInput("");
  }, [promptPreFill, isBusy, project?.id, files.length, entrySource]);

  useEffect(() => {
    if (!project?.id || typeof localStorage === "undefined") {
      setPlanData(null);
      return;
    }
    try {
      const raw = localStorage.getItem(PLAN_STORAGE_KEY(project.id));
      if (!raw) {
        setPlanData(null);
        return;
      }
      const parsed = JSON.parse(raw) as StoredPlan;
      setPlanData(parsed?.plan && typeof parsed.generatedAt === "number" ? parsed : null);
    } catch {
      setPlanData(null);
    }
  }, [project?.id]);

  // Pattern 4A: Reset persistedIndustry when project changes (prevents stale suggestions across projects)
  const prevProjectIdRef = useRef(project?.id);
  useEffect(() => {
    if (project?.id !== prevProjectIdRef.current) {
      prevProjectIdRef.current = project?.id;
      setPersistedIndustry(null);
    }
  }, [project?.id]);

  // Pattern 4A: Persist industry when planPreview.industry first arrives (survives edits within session)
  useEffect(() => {
    if (planPreview?.industry && !persistedIndustry) {
      setPersistedIndustry(planPreview.industry);
    }
  }, [planPreview?.industry, persistedIndustry]);

  // Auto-generate plan once when entering from "Build This Idea" (idea) or Create with substantial prompt, non-blocking
  useEffect(() => {
    if (!promptPreFill?.trim() || !project?.id || planData || didAutoPlanRef.current) return;
    const runForIdea = entrySource === "idea";
    const runForCreate = entrySource === "create" && isSubstantialPrompt(promptPreFill);
    if (!runForIdea && !runForCreate) return;
    didAutoPlanRef.current = true;
    const source = entrySource ?? "idea";
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("generate-plan", {
          body: { prompt: promptPreFill!.trim(), ideaContext: promptPreFill!.trim() },
        });
        if (error) throw error;
        const plan = data?.plan as StartupPlan | undefined;
        if (!plan || typeof plan !== "object") throw new Error("Invalid plan response");
        const stored: StoredPlan = {
          plan: {
            problem: typeof plan.problem === "string" ? plan.problem : "",
            icp: typeof plan.icp === "string" ? plan.icp : "",
            mvpFeatures: Array.isArray(plan.mvpFeatures) ? plan.mvpFeatures.filter((x): x is string => typeof x === "string") : [],
            monetization: typeof plan.monetization === "string" ? plan.monetization : "",
            keyScreens: Array.isArray(plan.keyScreens) ? plan.keyScreens.filter((x): x is string => typeof x === "string") : [],
            dataModel: Array.isArray(plan.dataModel) ? plan.dataModel.filter((x): x is string => typeof x === "string") : [],
            fileTree: Array.isArray(plan.fileTree) ? plan.fileTree.filter((x): x is string => typeof x === "string") : [],
          },
          generatedAt: Date.now(),
        };
        localStorage.setItem(PLAN_STORAGE_KEY(project.id), JSON.stringify(stored));
        setPlanData(stored);
        onPlanGenerated?.();
        onPlanAutoGenerated?.(source);
        toast.success("Plan ready");
      } catch (e) {
        console.error("[BuilderWorkspaceState] auto generate-plan failed", e);
        didAutoPlanRef.current = false;
      }
    })();
  }, [entrySource, promptPreFill, project?.id, planData, onPlanGenerated, onPlanAutoGenerated]);

  function formatRelativeTime(ts: number): string {
    const sec = Math.floor((Date.now() - ts) / 1000);
    if (sec < 10) return "Just now";
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const h = Math.floor(min / 60);
    return `${h}h ago`;
  }

  function statusLabel(s: string): string {
    const map: Record<string, string> = {
      generating: "Generating…",
      applying: "Applying…",
      building: "Validating build…",
      fixing: "Fixing…",
      polishing: "Polishing…",
      success: "Success",
      failed: "Failed",
    };
    return map[s] ?? s;
  }

  const scrollChatToBottom = useCallback(() => {
    const el = chatScrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      userHasScrolledUpRef.current = false;
    }
  }, []);

  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const SCROLL_THRESHOLD = 80;

  const handleChatScroll = useCallback(() => {
    const el = chatScrollContainerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
    if (nearBottom) {
      userHasScrolledUpRef.current = false;
      setShowScrollToLatest(false);
    } else {
      userHasScrolledUpRef.current = true;
      setShowScrollToLatest(true);
    }
  }, []);

  useEffect(() => {
    if (!userHasScrolledUpRef.current && chatScrollContainerRef.current) {
      chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const lastIdx = messages.length - 1;
    if (lastIdx >= 0 && messages[lastIdx]?.editPlan) {
      setExpandedPlanIndex(lastIdx);
    } else {
      setExpandedPlanIndex((prev) => {
        if (prev === null) return null;
        if (messages[prev]?.editPlan) return prev;
        return null;
      });
    }
  }, [messages]);

  // Preview load timeout: only start AFTER build is complete (success/idle), not during active generation.
  // The timeout fires 5 min after build completes if preview hasn't rendered yet.
  // IMPORTANT: Only start if buildStartedThisSession is true — don't show warning on page load.
  const buildComplete = builderStatus === "success" || builderStatus === "idle";
  useEffect(() => {
    // Don't start timeout if build is still in progress or hasn't started
    if (!isPreviewAvailable || !project?.id || !buildComplete || !buildStartedThisSession) {
      // Clear any existing timeout if build restarts
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      setPreviewLoadTimedOut(false);
      return;
    }
    // Build is complete and preview is available — start the timeout
    setPreviewLoadTimedOut(false);
    const tid = setTimeout(() => setPreviewLoadTimedOut(true), PREVIEW_LOAD_TIMEOUT_MS);
    loadTimeoutRef.current = tid;
    return () => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    };
  }, [isPreviewAvailable, previewKey, project?.id, buildComplete, buildStartedThisSession]);

  // Listen for render confirmation from __render.tsx via postMessage
  // Only clear timeout when app actually renders (not just when iframe HTML loads)
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (!data) return;
      // Check for telemetry or render ack with rendered === true
      const isTelemetry = data.type === "DAYONE_PREVIEW_TELEMETRY";
      const isAck = data.type === "DAYONE_RENDER_ACK";
      if ((isTelemetry || isAck) && data.rendered === true) {
        // App has rendered successfully — clear timeout
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
        setPreviewLoadTimedOut(false);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleIframeLoad = useCallback(() => {
    // Note: We DON'T clear the timeout here because the iframe HTML loading
    // doesn't mean the app has rendered. The timeout is cleared when we receive
    // DAYONE_PREVIEW_TELEMETRY or DAYONE_RENDER_ACK with rendered === true.
    onPreviewReady?.();
  }, [onPreviewReady]);

  const sendPrompt = useCallback(
    async (text: string) => {
      const t = (text ?? "").trim();
      if (!t) return;
      if (isBusy) return;
      try {
        await onSendMessage(t);
      } catch (e) {
        console.error("Run fix failed", e);
      }
    },
    [onSendMessage, isBusy]
  );

  const handleSubmit = () => {
    if (input.trim() && !isBusy) {
      onSendMessage(input.trim());
      setInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isBusy) handleSubmit();
    }
  };

  const handleQuickAction = (prompt: string) => {
    setInput(prompt);
    textareaRef.current?.focus();
  };

  const handlePresetChip = (text: string) => {
    setInput((prev) => (prev.trim() ? prev.trim() + " " + text : text));
    textareaRef.current?.focus();
  };

  const getFixPrompt = (): string => {
    const snippet = (runtimeErrorSnippet ?? "").trim().slice(0, 200);
    if (snippet) {
      return `Fix this runtime error and update the app. Error: ${snippet}. Search the codebase for where this is referenced and remove/replace it. Ensure the app runs without errors.`;
    }
    return "Fix the runtime error and update the app. Search the codebase for the cause and ensure the app runs without errors.";
  };

  const handleFillFixPrompt = () => {
    setInput(getFixPrompt());
    textareaRef.current?.focus();
  };

  const handleRunFix = () => {
    sendPrompt(getFixPrompt());
  };

  const fixPromptTrimmed = getFixPrompt().trim();

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: "#0f0f13" }}>
      {shouldShowOnboarding && onDismissOnboarding && (
        <WorkspaceOnboarding
          step={onboardingStep}
          targetRefs={[chatInputContainerRef, previewContainerRef, headerActionsRef]}
          onNext={() => setOnboardingStep((s) => (s < 2 ? s + 1 : s))}
          onSkip={onDismissOnboarding}
        />
      )}
      {/* Top Nav Bar */}
      <div className="h-14 flex items-center justify-between px-4 border-b" style={{ background: "rgba(237, 233, 248, 0.95)", backdropFilter: "blur(12px)", borderColor: "rgba(124, 106, 240, 0.15)" }}>
        <div className="flex items-center gap-3">
          <span className="text-lg" style={{ fontFamily: "'Instrument Serif', serif", color: "#18151f" }}>DayOne</span>
          <div className="w-px h-5" style={{ background: "rgba(124, 106, 240, 0.2)" }} />
          <ProjectSwitcher
            currentProject={project}
            projects={projects}
            onSelectProject={onSelectProject}
            onNewProject={onNewProject}
            onRenameProject={onRenameProject}
            onDeleteProject={onDeleteProject}
            onDuplicateProject={onDuplicateProject}
          />
        </div>
        <div ref={headerActionsRef} className="flex items-center gap-2">
          <VersionHistoryDialog
            snapshots={snapshots}
            isLoading={snapshotsLoading}
            onRestore={onRestoreSnapshot}
            onDelete={onDeleteSnapshot}
          />
          <ExplainAppDialog
            memory={memory}
            projectName={project.name}
            files={files}
            isLoading={isMemoryLoading}
          />
          {githubConnected && onOpenGitHubPush ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenGitHubPush}
              disabled={isBusy || githubPushRunning}
              className="h-8 w-8 p-0"
              style={{ color: "#6b5f8a" }}
              title={lastRepo ? `Push to ${lastRepo.owner}/${lastRepo.repo}` : "Push to GitHub"}
            >
              {githubPushRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Github className="h-3.5 w-3.5" />}
            </Button>
          ) : onOpenGitHubConnect && !githubConnected ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenGitHubConnect}
              className="h-8 w-8 p-0"
              style={{ color: "#6b5f8a" }}
              title="Connect GitHub"
            >
              <Github className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={onDeploy}
            disabled={isDeploying || !previewReady}
            title={!previewReady ? "Run a preview first" : "Deploy your app live"}
            className="gap-1.5 h-8 text-xs font-medium rounded-full px-4"
            style={{ background: "linear-gradient(135deg, #a898f8, #cc82e8)", color: "#fff", border: "none" }}
          >
            {isDeploying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
            Publish
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
      {/* Left: Chat Panel — lavender frosted glass */}
      <div className="w-80 h-full min-h-0 flex flex-col overflow-hidden" style={{ background: "rgba(237, 233, 248, 0.95)", borderRight: "1px solid rgba(255, 255, 255, 0.6)" }}>
        {/* Status Pills */}
        <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "rgba(124, 106, 240, 0.15)" }}>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{
              background: userStatus.tone === "busy" ? "rgba(124, 106, 240, 0.15)" : userStatus.tone === "error" ? "rgba(239, 68, 68, 0.15)" : "rgba(34, 197, 94, 0.15)",
              color: userStatus.tone === "busy" ? "#7c6af0" : userStatus.tone === "error" ? "#dc2626" : "#16a34a",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                background: userStatus.tone === "busy" ? "#7c6af0" : userStatus.tone === "error" ? "#dc2626" : "#16a34a",
                animation: userStatus.tone === "busy" ? "pulse 1.5s ease-in-out infinite" : "none",
              }}
            />
            {userStatus.label}
          </div>
          {files.length > 0 && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-mono"
              style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0", fontFamily: "'JetBrains Mono', monospace" }}
            >
              {files.length} files
            </span>
          )}
        </div>

        {/* Single status line — only show on error/warning */}
        {(userStatus.tone === "error" || userStatus.tone === "warning") && (
        <div className="px-4 py-2 border-b flex items-center gap-2 text-xs transition-opacity duration-200" style={{ borderColor: "rgba(124, 106, 240, 0.15)", background: "rgba(255, 255, 255, 0.5)" }}>
          {userStatus.tone === "warning" && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#d97706" }} />}
          {userStatus.tone === "error" && <XCircle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#dc2626" }} />}
          <span style={{ color: userStatus.tone === "error" ? "#dc2626" : "#d97706", fontFamily: "'DM Sans', sans-serif" }}>{userStatus.label}</span>
          {userStatus.tone === "error" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs gap-1 flex-shrink-0 ml-auto"
              style={{ color: "#7c6af0" }}
              onClick={() => buildErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
            >
              View error
            </Button>
          )}
        </div>
        )}

        {/* Chat: flex column — scrollable middle, pinned composer bottom */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Scrollable top: Build error, Report, Timeline, Messages */}
            <div
              ref={chatScrollContainerRef}
              onScroll={handleChatScroll}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            >
              <div className="p-4 space-y-4">
            {/* Self-healing status indicator */}
            {selfHealState && selfHealState.status === "fixing" && (
              <div className="p-3 rounded-xl transition-all duration-200 animate-fade-in" style={{ background: "rgba(255, 255, 255, 0.72)", border: "1px solid rgba(124, 106, 240, 0.3)" }}>
                <p className="text-xs font-medium mb-1 flex items-center gap-1.5" style={{ color: "#7c6af0", fontFamily: "'DM Sans', sans-serif" }}>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Auto-fixing error (attempt {selfHealState.attempts}/2)
                </p>
                {selfHealState.currentError && (
                  <p className="text-xs max-h-12 overflow-y-auto whitespace-pre-wrap break-words" style={{ color: "#6b5f8a" }}>
                    {selfHealState.currentError.message.slice(0, 200)}
                  </p>
                )}
              </div>
            )}
            {selfHealState && selfHealState.status === "fixed" && (
              <div className="p-3 rounded-xl transition-all duration-200 animate-fade-in" style={{ background: "rgba(34, 197, 94, 0.1)", border: "1px solid rgba(34, 197, 94, 0.3)" }}>
                <p className="text-xs font-medium flex items-center gap-1.5" style={{ color: "#16a34a", fontFamily: "'DM Sans', sans-serif" }}>
                  <CheckCircle2 className="h-3 w-3" />
                  Auto-fixed successfully
                </p>
              </div>
            )}

            {/* Build error in panel (surfaced from preview header) */}
            {/* Note: During "polishing" status, errors are hidden — only shown after all retries fail */}
            {builderStatus === "failed" && buildValidationError && (
              <div ref={buildErrorRef} className="p-3 rounded-xl transition-all duration-200 animate-fade-in" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
                <p className="text-xs font-medium mb-1" style={{ color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>Something went wrong</p>
                <p className="text-xs mb-2" style={{ color: "#6b5f8a" }}>
                  We couldn't complete your build. Try clicking "Fix errors" or start fresh.
                </p>
                {/* Collapsible technical details */}
                <Collapsible open={showTechnicalDetails} onOpenChange={setShowTechnicalDetails}>
                  <CollapsibleTrigger asChild>
                    <button className="text-xs flex items-center gap-1 mb-2 hover:opacity-80 transition-opacity" style={{ color: "#6b5f8a" }}>
                      <ChevronDown className={cn("h-3 w-3 transition-transform", showTechnicalDetails && "rotate-180")} />
                      {showTechnicalDetails ? "Hide" : "Show"} technical details
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <pre className="text-xs p-2 rounded-lg mb-2 max-h-32 overflow-y-auto whitespace-pre-wrap break-words" style={{ background: "rgba(0,0,0,0.2)", color: "#6b5f8a", fontFamily: "monospace" }}>
                      {buildValidationError}
                    </pre>
                  </CollapsibleContent>
                </Collapsible>
                <div className="flex flex-wrap gap-2">
                  {buildValidationBucket === "OUT_OF_SCOPE_EDIT" && onRetryWithinTargets && !isBusy && (
                    <Button variant="default" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ background: "#7c6af0", color: "#fff" }} onClick={onRetryWithinTargets}>
                      <RefreshCw className="h-3 w-3" />
                      Retry within targets
                    </Button>
                  )}
                  {onRetryLastChange && lastPrompt?.trim() && !isBusy && buildValidationBucket !== "OUT_OF_SCOPE_EDIT" && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ borderColor: "rgba(124, 106, 240, 0.3)", color: "#7c6af0" }} onClick={onRetryLastChange}>
                      <RefreshCw className="h-3 w-3" />
                      Retry last change
                    </Button>
                  )}
                  {onFixBuildClick && !fixAttempted && !isFixing && !isPolishing && (
                    <Button variant="secondary" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ background: "rgba(124, 106, 240, 0.15)", color: "#7c6af0" }} onClick={onFixBuildClick}>
                      <Wrench className="h-3 w-3" />
                      Fix errors
                    </Button>
                  )}
                  {onSelfHealFix && selfHealState?.canManualFix && !isFixing && !isPolishing && (
                    <Button variant="secondary" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ background: "rgba(124, 106, 240, 0.15)", color: "#7c6af0" }} onClick={onSelfHealFix}>
                      <Wrench className="h-3 w-3" />
                      Fix errors
                    </Button>
                  )}
                  {onStartFresh && !isBusy && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ color: "#6b5f8a" }} onClick={onStartFresh}>
                      <XCircle className="h-3 w-3" />
                      Start fresh
                    </Button>
                  )}
                  {isFixing && (
                    <span className="text-xs flex items-center gap-1.5" style={{ color: "#7c6af0" }}>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Fixing…
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Self-heal failed: runtime error that couldn't be auto-fixed */}
            {selfHealState?.status === "failed" && builderStatus !== "failed" && (
              <div ref={buildErrorRef} className="p-3 rounded-xl transition-all duration-200 animate-fade-in" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
                <p className="text-xs font-medium mb-1" style={{ color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>Runtime error detected</p>
                {selfHealState.currentError && (
                  <p className="text-xs mb-2 max-h-20 overflow-y-auto whitespace-pre-wrap break-words" style={{ color: "#6b5f8a" }}>
                    {selfHealState.currentError.message.slice(0, 300)}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {onSelfHealFix && (
                    <Button variant="secondary" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ background: "rgba(124, 106, 240, 0.15)", color: "#7c6af0" }} onClick={onSelfHealFix}>
                      <Wrench className="h-3 w-3" />
                      Fix errors
                    </Button>
                  )}
                  {onStartFresh && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ color: "#6b5f8a" }} onClick={onStartFresh}>
                      <XCircle className="h-3 w-3" />
                      Start fresh
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Build Report (after run completes) */}
            {buildReport && (
              <div className="p-3 rounded-xl" style={{ background: "rgba(255, 255, 255, 0.72)", border: "1px solid rgba(124, 106, 240, 0.2)" }}>
                <p className="text-xs font-medium mb-2" style={{ color: "#18151f", fontFamily: "'DM Sans', sans-serif" }}>Build Report</p>
                <div className="space-y-1.5 text-xs" style={{ color: "#6b5f8a" }}>
                  <p>
                    <span className="font-medium" style={{ color: "#18151f" }}>Status:</span>{" "}
                    {buildReport.status === "success" ? "Success" : buildReport.status === "failed" ? "Failed" : "Needs attention"}
                    {buildReport.errorBucket && (
                      <span className="ml-1" style={{ color: "#dc2626" }}>({buildReport.errorBucket})</span>
                    )}
                  </p>
                  <p>
                    <span className="font-medium" style={{ color: "#18151f" }}>File Targets:</span> {buildReport.targetPaths.length}
                    {buildReport.targetPaths.length > 0 && (
                      <span className="block mt-0.5 font-mono text-[11px] truncate" title={buildReport.targetPaths.join(", ")}>
                        {buildReport.targetPaths.slice(0, 3).join(", ")}
                        {buildReport.targetPaths.length > 3 ? "…" : ""}
                      </span>
                    )}
                  </p>
                  <p>
                    <span className="font-medium" style={{ color: "#18151f" }}>Files Changed:</span>{" "}
                    {(() => {
                      const visible = filterProtectedPaths(buildReport.changedPaths);
                      return visible.length === 0 ? "—" : (
                        <span title={visible.join("\n")}>
                          {visible.slice(0, 5).join(", ")}
                          {visible.length > 5 ? ` … +${visible.length - 5} more` : ""}
                        </span>
                      );
                    })()}
                  </p>
                  <p>
                    <span className="font-medium" style={{ color: "#18151f" }}>Preview:</span>{" "}
                    {buildReport.previewReadyAt != null ? "Ready" : buildReport.status === "failed" ? "Not available" : "Timed out"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {onCopyBuildReport && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ borderColor: "rgba(124, 106, 240, 0.3)", color: "#7c6af0" }} onClick={onCopyBuildReport}>
                      <Copy className="h-3 w-3" />
                      Copy report
                    </Button>
                  )}
                  {onRetryLastChange && lastPrompt?.trim() && !isBusy && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ borderColor: "rgba(124, 106, 240, 0.3)", color: "#7c6af0" }} onClick={onRetryLastChange}>
                      <RefreshCw className="h-3 w-3" />
                      Retry last
                    </Button>
                  )}
                  {onRevertLastChange && snapshots.length >= 2 && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" style={{ borderColor: "rgba(124, 106, 240, 0.3)", color: "#7c6af0" }} onClick={onRevertLastChange}>
                      <Undo2 className="h-3 w-3" />
                      Revert last
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Build Timeline (last 5 runs) */}
            {buildLog.length > 0 && (
              <div className="pb-2">
                <p className="text-xs font-medium mb-2" style={{ color: "#6b5f8a", fontFamily: "'DM Sans', sans-serif" }}>Build Timeline</p>
                <div className="space-y-2">
                  {buildLog.slice(0, 5).map((item) => {
                    const isFailed = item.status === "failed";
                    const isMostRecentFailed = isFailed && buildLog[0]?.id === item.id;
                    const showView = isFailed && (item.error?.length ?? 0) > 0;
                    const expanded = expandedErrorId === item.id;
                    return (
                      <div
                        key={item.id}
                        className="rounded-xl text-xs p-2"
                        style={{
                          background: isFailed ? "rgba(239, 68, 68, 0.1)" : "rgba(255, 255, 255, 0.6)",
                          border: isFailed ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid rgba(124, 106, 240, 0.15)",
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span style={{ color: "#6b5f8a" }}>{formatRelativeTime(item.ts)}</span>
                          <span style={{
                            fontWeight: 500,
                            color: item.status === "success" ? "#16a34a" : item.status === "failed" ? "#dc2626" : "#7c6af0",
                          }}>
                            {statusLabel(item.status)}
                          </span>
                        </div>
                        {item.status === "success" && item.summary && (
                          <p className="mt-1 line-clamp-2" style={{ color: "#6b5f8a" }}>{item.summary}</p>
                        )}
                        {isFailed && item.error && (
                          <>
                            <p className="mt-1 line-clamp-2 break-words" style={{ color: "#6b5f8a" }}>{item.error}</p>
                            {showView && (
                              <button
                                type="button"
                                className="mt-1 hover:underline flex items-center gap-0.5"
                                style={{ color: "#7c6af0" }}
                                onClick={() => setExpandedErrorId((id) => (id === item.id ? null : item.id))}
                              >
                                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                {expanded ? "Hide" : "View"}
                              </button>
                            )}
                            {expanded && item.error && (
                              <div className="mt-2 p-2 rounded-lg max-h-24 overflow-y-auto whitespace-pre-wrap break-words" style={{ background: "rgba(255, 255, 255, 0.8)", border: "1px solid rgba(124, 106, 240, 0.15)", color: "#6b5f8a" }}>
                                {item.error}
                              </div>
                            )}
                          </>
                        )}
                        {isMostRecentFailed && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {onRetryLastChange && (item.prompt?.trim() ?? lastPrompt?.trim()) && !isBusy && (
                              <Button variant="outline" size="sm" className="h-6 text-xs gap-1 rounded-full" style={{ borderColor: "rgba(124, 106, 240, 0.3)", color: "#7c6af0" }} onClick={onRetryLastChange}>
                                <RefreshCw className="h-3 w-3" />
                                Retry prompt
                              </Button>
                            )}
                            {onFixBuildClick && !fixAttempted && !isFixing && (
                              <Button variant="secondary" size="sm" className="h-6 text-xs gap-1 rounded-full" style={{ background: "rgba(124, 106, 240, 0.15)", color: "#7c6af0" }} onClick={onFixBuildClick}>
                                <Wrench className="h-3 w-3" />
                                Fix errors
                              </Button>
                            )}
                            {onStartFresh && !isBusy && (
                              <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 rounded-full" style={{ color: "#6b5f8a" }} onClick={onStartFresh}>
                                <XCircle className="h-3 w-3" />
                                Start fresh
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Messages / quick actions */}
            {messages.length === 0 ? (
              <div className="py-8">
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "linear-gradient(135deg, rgba(168, 152, 248, 0.3), rgba(204, 130, 232, 0.2))" }}>
                    <Sparkles className="h-7 w-7" style={{ color: "#7c6af0" }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: "#18151f", fontFamily: "'Instrument Serif', serif" }}>What would you like to build?</h3>
                  <p className="text-sm" style={{ color: "#6b5f8a", fontFamily: "'DM Sans', sans-serif" }}>
                    Describe any changes and I'll update your app instantly
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="space-y-2">
                  <p className="text-xs font-medium px-1" style={{ color: "#6b5f8a", fontFamily: "'DM Sans', sans-serif" }}>Quick actions</p>
                  <div className="flex flex-wrap gap-2">
                    {quickActions.map((action) => (
                      <button
                        key={action.label}
                        onClick={() => handleQuickAction(action.prompt)}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-3 py-1.5 transition-all disabled:opacity-50"
                        style={{
                          background: "rgba(255, 255, 255, 0.7)",
                          border: "1px solid rgba(124, 106, 240, 0.15)",
                          color: "#6b5f8a",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        <action.icon className="h-3.5 w-3.5" style={{ color: "#7c6af0" }} />
                        <span>{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message, idx) => {
                const ts = message.timestamp ? new Date(message.timestamp) : null;
                const timeStr = ts ? ts.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
                const changedFiles = message.editPlan?.edits?.map((e: any) => e.filePath?.split("/").pop()).filter(Boolean) as string[] | undefined;
                return (
                  <div
                    key={idx}
                    className={cn(
                      "animate-fade-in mb-1",
                      message.role === "user" ? "flex flex-col items-end" : "flex flex-col items-start"
                    )}
                  >
                    <div
                      className="max-w-[85%] px-3.5 py-2.5 leading-relaxed"
                      style={message.role === "user" ? {
                        background: "#18151f",
                        color: "#ffffff",
                        borderRadius: "14px 4px 14px 14px",
                        fontSize: "12px",
                        fontFamily: "'DM Sans', sans-serif",
                      } : {
                        background: "rgba(255, 255, 255, 0.65)",
                        border: "1px solid rgba(255, 255, 255, 0.9)",
                        borderRadius: "4px 14px 14px 14px",
                        color: "#18151f",
                        fontSize: "12px",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {message.editPlan ? (
                        <div className="space-y-3">
                          <p className="text-sm leading-relaxed">{message.content}</p>
                          {/* File change chips + orchestration status */}
                          {(changedFiles?.length > 0 || message.editPlan?.orchestration) && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {/* File count - prefer orchestration data for ground truth */}
                              {message.editPlan?.orchestration?.files ? (
                                <span className="text-xs" style={{ color: message.editPlan.orchestration.files.failed > 0 ? "#f59e0b" : "#6b5f8a" }}>
                                  Applied {message.editPlan.orchestration.files.applied}/{message.editPlan.orchestration.files.attempted} file{message.editPlan.orchestration.files.attempted !== 1 ? "s" : ""}
                                </span>
                              ) : changedFiles?.length > 0 ? (
                                <span className="text-xs" style={{ color: "#6b5f8a" }}>
                                  Applied {changedFiles.length} change{changedFiles.length !== 1 ? "s" : ""}
                                </span>
                              ) : null}

                              {/* Migration status */}
                              {message.editPlan?.orchestration?.migration?.status === "applied" && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                                  <Database className="h-3 w-3" />
                                  {message.editPlan.orchestration.migration.tables_created
                                    ? `${message.editPlan.orchestration.migration.tables_created} table${message.editPlan.orchestration.migration.tables_created !== 1 ? "s" : ""} created`
                                    : `${message.editPlan.orchestration.migration.statements_executed || 0} statement${(message.editPlan.orchestration.migration.statements_executed || 0) !== 1 ? "s" : ""} executed`
                                  }
                                </span>
                              )}
                              {message.editPlan?.orchestration?.migration?.status === "failed" && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md cursor-help"
                                  style={{ background: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.2)" }}
                                  title={message.editPlan.orchestration.migration.error || "Migration failed"}
                                >
                                  <XCircle className="h-3 w-3" />
                                  Migration failed
                                </span>
                              )}
                              {message.editPlan?.orchestration?.migration?.status === "skipped" && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md cursor-help"
                                  style={{ background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b", border: "1px solid rgba(245, 158, 11, 0.2)" }}
                                  title={message.editPlan.orchestration.migration.reason === "project_not_provisioned"
                                    ? "Provision a database first in Cloud > Database to apply migrations"
                                    : message.editPlan.orchestration.migration.reason || "Migration skipped"
                                  }
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  Migration skipped
                                </span>
                              )}

                              {/* File chips */}
                              {changedFiles?.map((f: string, fi: number) => (
                                <span key={fi} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0", border: "1px solid rgba(124, 106, 240, 0.2)" }}>
                                  <FileCode className="h-3 w-3" />
                                  {f}
                                </span>
                              ))}
                            </div>
                          )}
                          <Collapsible
                            open={expandedPlanIndex === idx}
                            onOpenChange={(open) => setExpandedPlanIndex(open ? idx : null)}
                            className="rounded-lg"
                            style={{ background: "rgba(124, 106, 240, 0.05)", border: "1px solid rgba(124, 106, 240, 0.15)" }}
                          >
                            <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium" style={{ color: "#6b5f8a" }}>
                              <span>Plan details</span>
                              <ChevronRight className="h-4 w-4 shrink-0 transition-transform [[data-state=open]_&]:rotate-90" />
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="px-3 pb-3 pt-0">
                                {message.editPlan.summary && (
                                  <p className="text-xs mb-2" style={{ color: "#6b5f8a" }}>{message.editPlan.summary}</p>
                                )}
                                <AIEditPlanDisplay
                                  plan={message.editPlan}
                                  onApply={onApplyEdits}
                                  isApplying={isApplying}
                                  currentFileContents={projectFileContents}
                                  onCancel={() => {
                                    onDismissPlan?.();
                                    setExpandedPlanIndex(null);
                                    requestAnimationFrame(() => textareaRef.current?.focus());
                                  }}
                                />
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                      )}
                    </div>
                    {timeStr && (
                      <span className="text-[10px] mt-1 px-1" style={{ color: "rgba(107, 95, 138, 0.5)" }}>{timeStr}</span>
                    )}
                  </div>
                );
              })
            )}
            {/* Live activity feed during generation */}
            {userStatus.tone === "busy" && (
              <div className="flex flex-col items-start animate-fade-in mb-1">
                <div className="max-w-[85%] shadow-sm rounded-2xl rounded-bl-sm px-4 py-3" style={{ background: "rgba(255, 255, 255, 0.9)", border: "1px solid rgba(124, 106, 240, 0.15)" }}>
                  <div className="space-y-2">
                    {/* V3 Streaming narrative messages — Pattern 3 two-line status cards */}
                    {narrativeMessages.length > 0 && (
                      <div className="space-y-2 border-l-2 pl-3 ml-1" style={{ borderColor: "rgba(124, 106, 240, 0.3)" }}>
                        {narrativeMessages.slice(-5).map((msg, i) => {
                          const isLast = i === Math.min(narrativeMessages.length, 5) - 1;
                          return (
                            <NarrativeStatusCard
                              key={msg.ts}
                              text={msg.text}
                              description={msg.description}
                              isActive={isLast}
                              showTypewriter={isLast}
                            />
                          );
                        })}
                      </div>
                    )}
                    {/* Step 1: Analyzing (show only if no narrative yet) */}
                    {narrativeMessages.length === 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      {builderStatus === "generating" && !streamingProgress?.completedFiles?.length ? (
                        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#7c6af0" }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#7c6af0" }} /></span>
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#16a34a" }} />
                      )}
                      <span style={{ color: "#6b5f8a" }}>Analyzing your request...</span>
                    </div>
                    )}
                    {/* Step 2: Planning (with plan preview when available) */}
                    {(streamingProgress?.phase === "generating" || builderStatus === "applying" || builderStatus === "building") && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          {streamingProgress?.phase === "generating" && !streamingProgress.completedFiles?.length && !planPreview ? (
                            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#7c6af0" }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#7c6af0" }} /></span>
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#16a34a" }} />
                          )}
                          <span style={{ color: "#6b5f8a" }}>{planPreview ? "Plan ready" : "Planning files..."}</span>
                        </div>
                        {/* Plan preview details */}
                        {planPreview && (
                          <div className="ml-5 pl-2 border-l-2 space-y-1 animate-fade-in" style={{ borderColor: "rgba(124, 106, 240, 0.2)" }}>
                            <p className="text-xs" style={{ color: "#18151f" }}>{planPreview.message}</p>
                            {planPreview.pages && planPreview.pages.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {planPreview.pages.slice(0, 6).map((page, i) => (
                                  <span key={i} className="inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0", border: "1px solid rgba(124, 106, 240, 0.2)" }}>
                                    {page}
                                  </span>
                                ))}
                                {planPreview.pages.length > 6 && (
                                  <span className="text-[10px]" style={{ color: "#6b5f8a" }}>+{planPreview.pages.length - 6} more</span>
                                )}
                              </div>
                            )}
                            {(planPreview.totalFiles || planPreview.components || planPreview.designSystem) && (
                              <p className="text-[10px]" style={{ color: "#6b5f8a" }}>
                                {planPreview.totalFiles} files • {planPreview.components} components • {planPreview.designSystem} style
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Step 3: Writing files (one per SSE event) — with diff indicator */}
                    {streamingProgress?.completedFiles?.map((f, i) => {
                      const isLast = i === streamingProgress!.completedFiles.length - 1 && builderStatus === "generating";
                      const fileName = f.label || f.path.split("/").pop() || f.path;
                      const actionColor = f.action === "create" ? { bg: "rgba(34, 197, 94, 0.1)", color: "#16a34a", border: "rgba(34, 197, 94, 0.2)" }
                        : f.action === "delete" ? { bg: "rgba(239, 68, 68, 0.1)", color: "#dc2626", border: "rgba(239, 68, 68, 0.2)" }
                        : { bg: "rgba(245, 158, 11, 0.1)", color: "#d97706", border: "rgba(245, 158, 11, 0.2)" };
                      const actionLabel = f.action === "create" ? "+" : f.action === "delete" ? "-" : "~";
                      return (
                        <div key={f.path} className="flex items-center gap-2 text-xs animate-fade-in">
                          {isLast ? (
                            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#7c6af0" }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#7c6af0" }} /></span>
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#16a34a" }} />
                          )}
                          <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded" style={{ background: actionColor.bg, color: actionColor.color, border: `1px solid ${actionColor.border}` }}>
                            {actionLabel}
                          </span>
                          <span className="font-mono" style={{ color: "#18151f" }}>{f.label || fileName}</span>
                          <span style={{ color: "rgba(107, 95, 138, 0.5)" }}>
                            {f.size < 1024 ? f.size + "B" : (f.size / 1024).toFixed(1) + "KB"}
                          </span>
                        </div>
                      );
                    })}
                    {/* Step 4: Bundling */}
                    {(builderStatus === "applying" || builderStatus === "building") && (
                      <div className="flex items-center gap-2 text-xs">
                        {builderStatus === "building" ? (
                          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#7c6af0" }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#7c6af0" }} /></span>
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#16a34a" }} />
                        )}
                        <span style={{ color: "#6b5f8a" }}>Bundling...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Ready confirmation */}
            {lastApplySummary && userStatus.tone !== "busy" && (
              <div className="flex flex-col items-start animate-fade-in mb-1">
                <div className="max-w-[85%] shadow-sm rounded-2xl rounded-bl-sm px-4 py-2.5" style={{ background: "rgba(255, 255, 255, 0.9)", border: "1px solid rgba(124, 106, 240, 0.15)" }}>
                  <div className="flex items-center gap-2 text-xs" style={{ color: "#16a34a" }}>
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="font-medium">{lastApplySummary}</span>
                  </div>
                </div>
              </div>
            )}
            {lastPreviewUpdatedAt != null && userStatus.tone !== "busy" && !lastApplySummary && (
              <div className="text-[10px] px-2" style={{ color: "rgba(107, 95, 138, 0.5)" }}>
                Preview updated {new Date(lastPreviewUpdatedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
              </div>

              {showScrollToLatest && (
                <div className="sticky bottom-0 left-0 right-0 flex justify-center py-2 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(237, 233, 248, 1), rgba(237, 233, 248, 0.95), transparent)" }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="pointer-events-auto gap-1.5 shadow-md rounded-full"
                    style={{ background: "rgba(255, 255, 255, 0.9)", color: "#7c6af0", border: "1px solid rgba(124, 106, 240, 0.2)" }}
                    onClick={() => { scrollChatToBottom(); setShowScrollToLatest(false); }}
                  >
                    <ArrowDownToLine className="h-3.5 w-3.5" />
                    Scroll to latest
                  </Button>
                </div>
              )}
            </div>

            {/* Composer — pinned at bottom, always visible */}
            <div ref={chatInputContainerRef} className="flex-shrink-0 p-4 border-t shrink-0" style={{ borderColor: "rgba(124, 106, 240, 0.15)", background: "rgba(255, 255, 255, 0.5)" }}>
          {/* Security alerts */}
          {securityAlerts.length > 0 && (
            <SecurityAlert
              matches={securityAlerts}
              onDismiss={onDismissSecurityAlerts ?? (() => {})}
              onViewFile={(filePath, lineNumber) => {
                const fileItem = files?.find(f => f.path === filePath || f.name === filePath);
                if (fileItem) {
                  onSelectFile?.(fileItem);
                }
              }}
            />
          )}
          {runtimeErrorSnippet?.trim() && (
            <div className="mb-3 p-2.5 rounded-xl flex flex-col gap-2" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
              <p className="text-xs font-medium" style={{ color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>Runtime error detected</p>
              <p className="text-xs line-clamp-2 break-words" style={{ color: "#6b5f8a" }}>
                {(runtimeErrorSnippet ?? "").trim().slice(0, 200)}
                {(runtimeErrorSnippet ?? "").trim().length > 200 ? "..." : ""}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" className="gap-1.5 h-7 text-xs rounded-full" style={{ background: "rgba(124, 106, 240, 0.15)", color: "#7c6af0" }} onClick={handleFillFixPrompt}>
                  <Wrench className="h-3 w-3" />
                  Fill fix prompt
                </Button>
                <Button type="button" variant="default" size="sm" className="gap-1.5 h-7 text-xs rounded-full" style={{ background: "#7c6af0", color: "#fff" }} onClick={handleRunFix} disabled={isBusy || !fixPromptTrimmed}>
                  {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  Run fix
                </Button>
              </div>
            </div>
          )}
          {/* Chat/Build mode toggle */}
          {onChatModeChange && (
            <div className="flex items-center gap-3 mb-3">
              <div className="inline-flex items-center rounded-full p-0.5" style={{ background: "rgba(255, 255, 255, 0.72)", border: "1px solid rgba(124, 106, 240, 0.2)" }}>
                <button
                  type="button"
                  onClick={() => onChatModeChange(false)}
                  className="text-xs font-medium px-3 py-1.5 rounded-full transition-all"
                  style={{
                    background: !chatMode ? "#ffffff" : "transparent",
                    color: !chatMode ? "#7c6af0" : "#6b5f8a",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Build
                </button>
                <button
                  type="button"
                  onClick={() => onChatModeChange(true)}
                  className="text-xs font-medium px-3 py-1.5 rounded-full transition-all"
                  style={{
                    background: chatMode ? "#ffffff" : "transparent",
                    color: chatMode ? "#7c6af0" : "#6b5f8a",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Chat
                </button>
              </div>
              {chatMode && (
                <span className="text-[10px]" style={{ color: "#6b5f8a" }}>Talk without making changes</span>
              )}
            </div>
          )}
          {/* Quick action pills — contextual (post-build) or static (pre-build) */}
          {!chatMode && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {persistedIndustry ? (
              // Post-build: industry-specific suggestions with Lightbulb icon
              (CONTEXTUAL_SUGGESTIONS[persistedIndustry] || GENERIC_SUGGESTIONS).map((suggestion) => (
                <button
                  key={suggestion.label}
                  type="button"
                  onClick={() => handleQuickAction(suggestion.prompt)}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 font-sans text-xs px-3 py-1.5 transition-all disabled:opacity-50 disabled:pointer-events-none"
                  style={{
                    background: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(124, 106, 240, 0.15)",
                    color: "#6b5f8a",
                    borderRadius: "20px",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <Lightbulb className="h-3.5 w-3.5" style={{ color: "#7c6af0" }} />
                  <span>{suggestion.label}</span>
                </button>
              ))
            ) : (
              // Pre-build: static pills with emoji
              STATIC_PILLS.map((pill) => (
                <button
                  key={pill.label}
                  type="button"
                  onClick={() => handlePresetChip(pill.label)}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 font-sans text-xs px-3 py-1.5 transition-all disabled:opacity-50 disabled:pointer-events-none"
                  style={{
                    background: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(124, 106, 240, 0.15)",
                    color: "#6b5f8a",
                    borderRadius: "20px",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <span className="text-[10px]">{pill.emoji}</span>
                  {pill.label}
                </button>
              ))
            )}
          </div>
          )}
          {/* Input bar */}
          <div
            className="relative shadow-sm transition-all duration-200"
            style={{
              background: isBusy ? "rgba(124, 106, 240, 0.05)" : "rgba(255, 255, 255, 0.85)",
              border: isBusy ? "1px solid rgba(124, 106, 240, 0.3)" : "1px solid rgba(255, 255, 255, 0.95)",
              borderRadius: "14px",
              backdropFilter: "blur(8px)",
            }}
          >
            {isBusy ? (
              <div className="flex items-center gap-2 px-4 py-3.5 min-h-[52px]">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#7c6af0" }} />
                <span className="text-sm" style={{ color: "#6b5f8a", fontFamily: "'DM Sans', sans-serif" }}>{chatLoading ? "Thinking..." : "Generating..."}</span>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={chatMode ? "Ask a question about your app..." : "Describe what you want to build or change..."}
                rows={2}
                disabled={isBusy}
                className="w-full min-h-[52px] px-4 py-3 pr-12 text-sm bg-transparent resize-none focus:outline-none"
                style={{ color: "#18151f", fontFamily: "'DM Sans', sans-serif", borderRadius: "14px" }}
              />
            )}
            <Button
              size="icon"
              onClick={handleSubmit}
              disabled={!input.trim() || isBusy}
              className="absolute right-2.5 bottom-2.5 transition-all duration-200"
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                background: input.trim() && !isBusy ? "#18151f" : "rgba(24, 21, 31, 0.3)",
                color: "#fff",
              }}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {/* Action row */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              {onRetryLastChange && lastPrompt?.trim() && (
                <Button variant="ghost" size="sm" onClick={onRetryLastChange} disabled={isBusy} className="gap-1 h-7 text-xs rounded-full" style={{ color: "#6b5f8a" }}>
                  <RefreshCw className="h-3 w-3" />
                  Retry
                </Button>
              )}
              {onRevertLastChange && snapshots.length >= 2 && (
                <Button variant="ghost" size="sm" onClick={onRevertLastChange} disabled={isBusy} className="gap-1 h-7 text-xs rounded-full" style={{ color: "#6b5f8a" }} title="Restore previous version">
                  <Undo2 className="h-3 w-3" />
                  Undo
                </Button>
              )}
            </div>
            <span className="text-[10px]" style={{ color: "rgba(107, 95, 138, 0.4)" }}>
              Enter to send · Shift+Enter for newline
            </span>
          </div>
        </div>
          </div>
      </div>

      {/* Right: Live Preview — dark background */}
      <div ref={previewContainerRef} className="flex-1 flex flex-col min-w-0" style={{ background: "#0f0f13" }}>
        {/* Preview Header */}
        <div style={{ background: "#1a1a1f", borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
          <div className="h-12 flex items-center justify-between px-4 gap-2">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ background: "#ff5f57" }} />
                <div className="w-3 h-3 rounded-full" style={{ background: "#febc2e" }} />
                <div className="w-3 h-3 rounded-full" style={{ background: "#28c840" }} />
              </div>
              <span className="text-sm font-medium" style={{ color: "rgba(255, 255, 255, 0.6)" }}>Live Preview</span>

              {/* Device Preview Toggle */}
              <div className="flex items-center rounded-md p-0.5 gap-0.5" style={{ background: "rgba(255, 255, 255, 0.08)" }}>
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className="p-1.5 rounded transition-colors"
                  style={{
                    background: previewDevice === 'desktop' ? "#7c6af0" : "transparent",
                    color: previewDevice === 'desktop' ? "#fff" : "rgba(255, 255, 255, 0.5)",
                  }}
                  title="Desktop"
                >
                  <Monitor className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className="p-1.5 rounded transition-colors"
                  style={{
                    background: previewDevice === 'tablet' ? "#7c6af0" : "transparent",
                    color: previewDevice === 'tablet' ? "#fff" : "rgba(255, 255, 255, 0.5)",
                  }}
                  title="Tablet"
                >
                  <Tablet className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className="p-1.5 rounded transition-colors"
                  style={{
                    background: previewDevice === 'mobile' ? "#7c6af0" : "transparent",
                    color: previewDevice === 'mobile' ? "#fff" : "rgba(255, 255, 255, 0.5)",
                  }}
                  title="Mobile"
                >
                  <Smartphone className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Edit Mode toggle */}
              <button
                onClick={() => setVisualEditMode(!visualEditMode)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all"
                style={{
                  background: visualEditMode ? "rgba(124, 106, 240, 0.2)" : "transparent",
                  color: visualEditMode ? "#a898f8" : "rgba(255, 255, 255, 0.5)",
                  border: visualEditMode ? "1px solid rgba(124, 106, 240, 0.4)" : "1px solid transparent",
                }}
                title={visualEditMode ? "Exit Edit Mode" : "Edit Mode — click elements to edit"}
              >
                <MousePointer className="h-3.5 w-3.5" />
                Edit
              </button>

              {/* Design Panel toggle */}
              <button
                onClick={() => setDesignPanelOpen(!designPanelOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all"
                style={{
                  background: designPanelOpen ? "rgba(204, 130, 232, 0.2)" : "transparent",
                  color: designPanelOpen ? "#cc82e8" : "rgba(255, 255, 255, 0.5)",
                  border: designPanelOpen ? "1px solid rgba(204, 130, 232, 0.4)" : "1px solid transparent",
                }}
                title={designPanelOpen ? "Close Design Panel" : "Design — colors, fonts, radius"}
              >
                <Palette className="h-3.5 w-3.5" />
                Design
              </button>

              {/* Divider */}
              <div className="w-px h-5" style={{ background: "rgba(255, 255, 255, 0.15)" }} />

              {/* Panel overlay buttons — Files, Cloud, Versions, Context */}
              {[
                { id: 'files' as const, label: 'Files', icon: FolderTree },
                { id: 'cloud' as const, label: 'Cloud', icon: Cloud },
                { id: 'versions' as const, label: 'Versions', icon: History },
                { id: 'knowledge' as const, label: 'Context', icon: BookOpen },
              ].map((panel) => (
                <button
                  key={panel.id}
                  onClick={() => setActiveOverlay(activeOverlay === panel.id ? null : panel.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all"
                  style={{
                    background: activeOverlay === panel.id ? "rgba(124, 106, 240, 0.2)" : "transparent",
                    color: activeOverlay === panel.id ? "#a898f8" : "rgba(255, 255, 255, 0.5)",
                    border: activeOverlay === panel.id ? "1px solid rgba(124, 106, 240, 0.4)" : "1px solid transparent",
                  }}
                  title={panel.label}
                >
                  <panel.icon className="h-3.5 w-3.5" />
                  {panel.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {onRefreshPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefreshPreview}
                  className="text-xs gap-1.5 h-8"
                  style={{ color: "rgba(255, 255, 255, 0.6)" }}
                  title="Refresh preview"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh
                </Button>
              )}

              {onExportZip && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onExportZip}
                  disabled={isBusy}
                  className="text-xs gap-1.5 h-8"
                  style={{ color: "rgba(255, 255, 255, 0.6)" }}
                  title="Download project as .zip"
                >
                  <Download className="h-3.5 w-3.5" />
                  ZIP
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      const url = (previewUrl && previewUrl.trim()) ? previewUrl : `${window.location.origin}/landing`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-2" />
                    Open preview in new tab
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      try {
                        (iframeRef as React.RefObject<HTMLIFrameElement | null>)?.current?.contentWindow?.location?.reload();
                      } catch (e) {
                        console.warn("[preview] hard reload failed", e);
                      }
                    }}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-2" />
                    Hard reload
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setApiKeysOpen(true)}>
                    <Key className="h-3.5 w-3.5 mr-2" />
                    API Keys
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Status bar */}
          <div className="h-10 px-4 flex items-center gap-4 border-t font-mono text-[10px]" style={{ background: "#141418", borderColor: "rgba(255, 255, 255, 0.05)" }}>
            <span className="flex items-center gap-1.5" style={{ color: "#28c840" }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#28c840", boxShadow: "0 0 8px rgba(40, 200, 64, 0.5)" }} />
              Sandbox ready
            </span>
            <div className="w-px h-3.5" style={{ background: "rgba(255, 255, 255, 0.1)" }} />
            <span style={{ color: "rgba(255, 255, 255, 0.4)" }}>⚡ esbuild</span>
            <span style={{ color: "rgba(255, 255, 255, 0.4)" }}>📦 React 18 · Tailwind 3.4</span>
            <div className="flex-1" />
            {selfHealState?.status === "fixing" && (
              <span className="flex items-center gap-1.5 text-xs flex-shrink-0" style={{ color: "#7c6af0" }}>
                <Loader2 className="h-3 w-3 animate-spin" />
                Auto-fixing…
              </span>
            )}
            {lastDeployUrl && userStatus.tone !== "error" && (
              <a
                href={lastDeployUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs hover:underline truncate max-w-[220px] flex-shrink-0"
                style={{ color: "#a898f8" }}
                title={lastDeployUrl}
              >
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{lastDeployUrl.replace(/^https?:\/\//, '')}</span>
              </a>
            )}
            <span className="rounded px-2 py-0.5 font-mono text-[10px]" style={{ background: "rgba(124, 106, 240, 0.15)", border: "1px solid rgba(124, 106, 240, 0.25)", color: "#a898f8" }}>
              {files.length} files
            </span>
          </div>
        </div>

        {/* Preview Iframe or empty state - only show iframe when preview has been booted */}
        <PreviewErrorBoundary sessionId={previewSessionId ?? project?.id} onRetry={onRefreshPreview}>
        <div ref={iframeContainerRef} className="flex-1 min-h-0 min-w-0 relative overflow-hidden" style={{ margin: "12px", borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.1)", background: "#0f0f13" }}>
          {isPreviewAvailable ? (
            previewLoadTimedOut ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8" style={{ background: "rgba(15, 15, 19, 0.8)" }}>
                <div className="space-y-4 max-w-sm">
                  <h3 className="text-base font-semibold" style={{ color: "#fff" }}>Preview taking longer than expected</h3>
                  <p className="text-sm" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    Click Refresh to reload the preview, or continue in the workspace.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {onRefreshPreview && (
                      <Button variant="default" size="sm" className="gap-1.5 rounded-full" style={{ background: "#7c6af0", color: "#fff" }} onClick={onRefreshPreview}>
                        <RefreshCw className="h-3.5 w-3.5" />
                        Refresh
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="rounded-full" style={{ borderColor: "rgba(255, 255, 255, 0.2)", color: "rgba(255, 255, 255, 0.7)" }} onClick={() => setPreviewLoadTimedOut(false)}>
                      Continue anyway
                    </Button>
                  </div>
                </div>
              </div>
            ) : (() => {
              const effectiveSessionId = getOrCreateActiveSessionId(previewSessionId ?? project?.id ?? null);
              const debug = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
              const originalSrc = `/__preview?sessionId=${encodeURIComponent(effectiveSessionId)}&k=${previewKey}${debug ? "&debug=1" : ""}`;
              const previewSrc = originalSrc ? `${originalSrc}${originalSrc.includes("?") ? "&" : "?"}t=${previewKey}` : originalSrc;
              if (debug) console.log("[BuilderWorkspaceState] iframe src", previewSrc, "sessionId", effectiveSessionId);

              const iframeEl = (
                <iframe
                  key={previewKey}
                  ref={iframeRef as any}
                  src={previewSrc}
                  className="w-full h-full border-0"
                  title="Preview"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  onLoad={handleIframeLoad}
                />
              );

              // Floating refresh button component for reuse
              const floatingRefreshBtn = onRefreshPreview && (
                <button
                  onClick={onRefreshPreview}
                  className="absolute top-3 right-3 z-10 p-2 rounded-full transition-all duration-200 hover:scale-105"
                  style={{
                    background: "rgba(15, 15, 19, 0.7)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                  title="Refresh preview"
                >
                  <RefreshCw className="h-4 w-4" style={{ color: "rgba(255, 255, 255, 0.7)" }} />
                </button>
              );

              if (previewDevice === 'mobile') {
                return (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "#0f0f13" }}>
                    {floatingRefreshBtn}
                    <div className="w-[390px] h-[844px] max-h-[calc(100%-32px)] rounded-[40px] border-[6px] bg-white shadow-2xl overflow-hidden flex flex-col" style={{ borderColor: "#2a2a30" }}>
                      <div className="w-full h-full overflow-auto">{iframeEl}</div>
                    </div>
                  </div>
                );
              }
              if (previewDevice === 'tablet') {
                return (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "#0f0f13" }}>
                    {floatingRefreshBtn}
                    <div className="w-[768px] max-w-[calc(100%-32px)] h-[calc(100%-32px)] rounded-[20px] border-[4px] bg-white shadow-2xl overflow-hidden" style={{ borderColor: "#2a2a30" }}>
                      {iframeEl}
                    </div>
                  </div>
                );
              }

              return (
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  {iframeEl}
                  {floatingRefreshBtn}
                </div>
              );
            })()
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div className="space-y-4 max-w-sm">
                <h3 className="text-base font-semibold" style={{ color: "#fff" }}>No preview yet</h3>
                <p className="text-sm" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                  Send a prompt to generate your app, then the preview will appear here.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button
                    variant="default"
                    size="sm"
                    className="gap-1.5 rounded-full"
                    style={{ background: "linear-gradient(135deg, #a898f8, #cc82e8)", color: "#fff" }}
                    onClick={() => textareaRef.current?.focus()}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Focus chat
                  </Button>
                  {previewReady && onRefreshPreview && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 rounded-full"
                      style={{ borderColor: "rgba(255, 255, 255, 0.2)", color: "rgba(255, 255, 255, 0.7)" }}
                      onClick={onRefreshPreview}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Reload preview
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Floating file checklist overlay — z-50 to stay above spinner */}
          {(builderStatus === "generating" || builderStatus === "applying" || builderStatus === "building") && streamingProgress && (() => {
            const completed = streamingProgress.completedFiles?.length ?? 0;
            const total = streamingProgress.totalExpectedFiles ?? 1;
            const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
            return (
              <div
                className="absolute z-50 bottom-4 left-4 w-56 rounded-xl p-3 shadow-xl"
                style={{ background: "rgba(26, 26, 31, 0.95)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 106, 240, 0.3)", maxHeight: '300px', overflowY: 'auto' }}
              >
                {/* Progress bar */}
                <div className="h-0.5 rounded-full mb-3 overflow-hidden" style={{ background: "rgba(255, 255, 255, 0.1)" }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{ background: "linear-gradient(90deg, #a898f8, #cc82e8)", width: `${pct}%` }} />
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                  Files Generated
                </div>
                <div className="flex items-center justify-between font-mono text-[10px] mb-2">
                  <span style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    {completed} / {streamingProgress.totalExpectedFiles ?? '?'} files
                  </span>
                  <span style={{ color: "#a898f8" }}>
                    {builderStatus === "generating" ? "writing..." : builderStatus === "applying" ? "applying..." : "bundling..."}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {streamingProgress.completedFiles?.slice(-8).map((f, i) => {
                    const displayLabel = f.label || (() => {
                      const fileName = f.path.split("/").pop() || f.path;
                      const baseName = fileName.replace(/\.(ts|tsx|js|jsx|css|json|html)$/, "");
                      return baseName.replace(/([a-z])([A-Z])/g, "$1 $2");
                    })();
                    const isActive = i === (streamingProgress.completedFiles?.slice(-8).length ?? 0) - 1 && builderStatus === "generating";
                    return (
                      <div key={f.path} className="flex items-center gap-1.5 text-[10px]">
                        {isActive ? (
                          <span className="inline-block w-2.5 h-2.5 border border-t-transparent rounded-full animate-spin flex-shrink-0" style={{ borderColor: "#a898f8", borderTopColor: "transparent" }} />
                        ) : (
                          <span className="flex-shrink-0" style={{ color: "#28c840" }}>✓</span>
                        )}
                        <span style={{ color: isActive ? "#a898f8" : "#28c840" }}>{displayLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Polishing overlay — hides preview errors during auto-fix retries (builderStatus=polishing OR selfHealState=fixing) */}
          {showPolishingOverlay && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center" style={{ background: "rgba(15, 15, 19, 0.95)" }}>
              <Loader2 className="h-8 w-8 animate-spin mb-4" style={{ color: "#7c6af0" }} />
              <p className="text-sm font-medium" style={{ color: "#fff" }}>Polishing your app…</p>
              <p className="text-xs mt-2" style={{ color: "rgba(255, 255, 255, 0.5)" }}>Making final adjustments</p>
            </div>
          )}

          {/* Visual Editor overlay — renders highlight + toolbar over the preview */}
          <VisualEditor
            iframeRef={iframeRef}
            containerRef={iframeContainerRef}
            editMode={visualEditMode}
            onSendEdit={(msg) => onSendMessage(msg)}
            previewKey={previewKey}
          />

          {/* Design Panel — slides in from right over preview */}
          {designPanelOpen && (
            <DesignPanel
              iframeRef={iframeRef}
              onClose={() => setDesignPanelOpen(false)}
            />
          )}

          {/* Panel Overlays — full-width slide-in from right */}
          {activeOverlay && (
            <>
              {/* Backdrop — click to close */}
              <div
                className="absolute inset-0 z-40"
                style={{ background: "rgba(0, 0, 0, 0.5)" }}
                onClick={() => setActiveOverlay(null)}
              />

              {/* Overlay Panel */}
              <div
                className="absolute top-0 right-0 bottom-0 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200"
                style={{
                  width: activeOverlay === 'cloud' ? '560px' : '400px',
                  background: "#0f0f13",
                  borderLeft: "1px solid rgba(124, 106, 240, 0.2)",
                }}
              >
                {/* Overlay Header */}
                <div className="h-12 flex items-center justify-between px-4 flex-shrink-0" style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
                  <span className="text-sm font-medium" style={{ color: "rgba(255, 255, 255, 0.9)", fontFamily: "'DM Sans', sans-serif" }}>
                    {activeOverlay === 'files' && 'Files'}
                    {activeOverlay === 'cloud' && 'Cloud'}
                    {activeOverlay === 'versions' && 'Versions'}
                    {activeOverlay === 'knowledge' && 'Context'}
                  </span>
                  <button
                    onClick={() => setActiveOverlay(null)}
                    className="p-1.5 rounded-md transition-colors hover:bg-white/10"
                    style={{ color: "rgba(255, 255, 255, 0.6)" }}
                    title="Close panel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Overlay Content */}
                <div className="flex-1 min-h-0 overflow-hidden">
                  {/* Files Panel */}
                  {activeOverlay === 'files' && (
                    <div className="flex flex-col h-full">
                      <ScrollArea className="h-48 border-b flex-shrink-0" style={{ borderColor: "rgba(255, 255, 255, 0.08)" }}>
                        <div className="p-2 space-y-0.5">
                          {files.filter(f => !f.isFolder).map((file) => (
                            <button
                              key={file.name}
                              onClick={() => onSelectFile(file.path)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left"
                              style={{
                                background: selectedFile === file.path ? "rgba(124, 106, 240, 0.15)" : "transparent",
                                color: selectedFile === file.path ? "#a898f8" : "rgba(255, 255, 255, 0.6)",
                                fontFamily: "'DM Sans', sans-serif",
                              }}
                            >
                              <FileCode className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate">{file.name.split("/").pop()}</span>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                      <div className="flex-1 min-h-0">
                        {selectedFile ? (
                          <Editor
                            height="100%"
                            language={getLanguage(selectedFile)}
                            value={editorContent}
                            onChange={(v) => onEditorChange(v || "")}
                            onMount={onEditorMount}
                            theme="vs-dark"
                            options={{
                              minimap: { enabled: false },
                              fontSize: 12,
                              lineNumbers: "on",
                              scrollBeyondLastLine: false,
                              automaticLayout: true,
                              tabSize: 2,
                              wordWrap: "on",
                              padding: { top: 12, bottom: 12 },
                              folding: true,
                              glyphMargin: false,
                              lineDecorationsWidth: 8,
                              lineNumbersMinChars: 3,
                            }}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-sm" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                            Select a file to edit
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cloud Panel */}
                  {activeOverlay === 'cloud' && (
                    <DayOneCloudPanel
                      projectId={project?.id ?? ""}
                      deployUrl={lastDeployUrl}
                      onSendMessage={onSendMessage}
                    />
                  )}

                  {/* Versions Panel */}
                  {activeOverlay === 'versions' && (
                    <VersionHistoryPanel
                      snapshots={snapshots}
                      isLoading={snapshotsLoading}
                      onRestore={onRestoreSnapshot}
                      onDelete={onDeleteSnapshot}
                    />
                  )}

                  {/* Context/Knowledge Panel */}
                  {activeOverlay === 'knowledge' && (
                    <ProjectKnowledgePanel projectId={project?.id ?? null} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        </PreviewErrorBoundary>

      </div>

      </div>

      {/* API Key Manager Dialog */}
      <ApiKeyManager
        open={apiKeysOpen}
        onOpenChange={setApiKeysOpen}
        projectId={project?.id ?? null}
      />
    </div>
  );
}
